import React from 'react'
import { useNavigate } from 'react-router-dom'
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import { orderBy } from 'lodash'
import {
  $DCprimaryColor,
  DcBreadcrumbs,
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcIcon,
  DcInput,
  DcTag,
  DcText,
  PixelCurrency,
  PixelDate,
  PixelFlexBox,
  PixelIcon,
  PixelPageContainer,
  PixelTable,
  Section,
  SectionActions,
  SectionContent,
  colors,
} from 'pixel-base'
import queryString from 'query-string'
import styled from 'styled-components'

import { getInvoices, getInvoiceStats } from 'utils/api-routes.util'

import InvoiceDetail from './components/InvoiceDetails'
import InvoicesStats from './components/InvoicesStats'

const breadcrumbs = [
  {
    name: 'Invoices',
    active: true,
  },
]
const tenant_info: any = JSON.parse(localStorage.getItem('tenant_info') || '{}')
const columns = [
  {
    dataKey: 'store_order_id',
    name: 'Invoice #',
    sortable: true,
    selector: (row) => row.store_order_id || '--',
    sortField: 'store_order_id',
  },
  {
    dataKey: 'invoice_status',
    name: 'Status',
    sortable: true,
    selector: (row) => (
      <DcTag
        textColor={
          row.invoice_status == 'open' ? colors.warning : colors.danger
        }
        customBackgroundColor={
          row.invoice_status == 'open'
            ? colors.warningLight
            : colors.dangerLight
        }
      >
        {row.invoice_status}
      </DcTag>
    ),
    sortField: 'invoice_status',
  },
  {
    dataKey: 'date_created',
    name: 'Date',
    sortable: true,
    selector: (row) => (
      <PixelDate
        className="textSmall"
        value={row.date_created}
        format={'pixelStandard'}
      />
    ),
    sortField: 'date_created',
  },
  {
    dataKey: 'payment_due_date',
    name: 'Due Date',
    sortable: true,
    selector: (row) =>
      row.payment_due_date ? (
        <PixelDate
          className="textSmall"
          value={row.payment_due_date}
          format={'pixelStandard'}
        />
      ) : (
        '--'
      ),
    sortField: 'payment_due_date',
  },
  {
    dataKey: 'full_name',
    name: 'Customer',
    sortable: true,
    selector: (row) =>
      (
        <CustomerInfo>
          {row.first_name} {row.last_name}
        </CustomerInfo>
      ) || '--',
    sortField: 'first_name',
  },
  {
    dataKey: 'city',
    name: 'City',
    sortable: true,
    selector: (row) => row.city || '--',
    sortField: 'city',
  },
  {
    dataKey: 'total',
    width: '11%',
    name: `Amount (${tenant_info.default_currency_symbol})`,
    sortable: true,
    selector: (row) =>
      <PixelCurrency hideSymbol={true} value={row.total} /> || '0',
    sortField: 'total',
  },
  {
    dataKey: 'tracking_id',
    name: 'Tracking #',
    sortable: true,
    selector: (row) => row.tracking_id || '--',
    sortField: 'tracking_id',
  },
  {
    dataKey: 'tracking_status',
    name: 'Tracking Status',
    sortable: true,
    selector: (row) => row.tracking_status || '--',
    sortField: 'tracking_status',
  },
  {
    dataKey: 'invoice_payment_status',
    name: 'Payment Status',
    sortable: true,
    selector: (row) => (
      <DcTag
        textColor={
          row.invoice_payment_status === 'paid' ? colors.warning : colors.danger
        }
        customBackgroundColor={
          row.invoice_payment_status === 'paid'
            ? colors.warningLight
            : colors.dangerLight
        }
      >
        {row.invoice_payment_status}
      </DcTag>
    ),
    sortField: 'invoice_payment_status',
  },
]

const Invoice = () => {
  const navigate = useNavigate()
  const [invoices, setInvoices] = React.useState<any>([])
  const [invoiceId, setInvoiceId] = React.useState('new')
  const [showModal, setShowModal] = React.useState(false)

  const handleSort = async (column, sortDirection) => {
    filters.sort_by = column.sortField
    filters.sort_order = sortDirection
    setTableLoading(true)
    getInvoices(filters).subscribe((res: any) => {
      res.response.data.map((item: any) => {
        item.city = item.city.charAt(0).toUpperCase() + item.city.slice(1)
        item.first_name =
          item.first_name.charAt(0).toUpperCase() + item.first_name.slice(1)
      })
      setInvoices(orderBy(res.response.data, column.sortField, sortDirection))
      setTableLoading(false)
    })
  }

  const [invoiceStats, setInvoiceStats] = React.useState<any>({
    total: { count: 0, amount: 0 },
    open: { count: 0, amount: 0 },
    closed: { count: 0, amount: 0 },
    overdue: { count: 0, amount: 0 },
  })
  const [tableLoading, setTableLoading] = React.useState(false)
  const [totalRows, setTotalRows] = React.useState(0)

  const p = queryString.parse(window.location.search)
  const [filters, setFilters] = React.useState({
    search: p?.search || null,
    status: p?.status || null,
    page: p?.page || 0,
    page_size: p?.page_size || 50,
    sort_by: p?.sort_by || null,
    sort_order: p?.sort_order || 'desc',
  })

  const handlePageChange = (page) => {
    if (filters.page === page - 1) return
    setFilters({
      ...filters,
      page: page - 1,
    })
  }

  const handlePerRowsChange = async (newPerPage) => {
    setFilters({
      ...filters,
      page_size: newPerPage,
    })
  }

  React.useEffect(() => {
    fetchInvoiceStats()
  }, [])

  const fetchInvoiceStats = () => {
    getInvoiceStats().subscribe((res: any) => {
      if (res?.response?.data?.stats) {
        setInvoiceStats(res.response.data.stats)
      }
    })
  }

  const getAllInvoices = () => {
    setTableLoading(true)
    getInvoices(filters).subscribe((res: any) => {
      setInvoices(res.response.data)
      setTotalRows(res.response._meta.total)
      setTableLoading(false)
    })
  }

  const onRowClicked = (row: any) => {
    setInvoiceId(row.invoice_id)
    setShowModal(true)
  }

  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false)

  React.useEffect(() => {
    navigate({
      pathname: '/invoice',
      search: queryString.stringify(filters),
    })
    getAllInvoices()
  }, [filters])

  return (
    <>
      <DcFlexBox
        padding="0px 20px"
        height="60px"
        style={{
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
        }}
        alignItems="center"
      >
        <DcFlexBox alignItems="center" width="auto">
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
          <SectionActions />
        </DcFlexBox>
        <DcDiv>
          <ProfileSection />
        </DcDiv>
      </DcFlexBox>
      <DcFlexBox height="auto" padding="5px 20px">
        <DcFlexBox height="auto" gap="5px" width="30%">
          <DcInput
            inputSize="sm"
            style={{ backgroundColor: colors.grayLighter }}
            showsearchicon={0}
            placeholder="Search"
            name="search"
            onChange={(e) => {
              setResetPaginationToggle(e.target.value.length > 0)
              setFilters({ ...filters, [e.target.name]: e.target.value })
            }}
            value={filters.search}
          />
          <DcDropDown
            dropdownSize="sm"
            placeholder="Filter By Status"
            options={[
              {
                label: 'Filter By Status',
                value: '',
              },
              {
                label: 'Open',
                value: 'open',
              },
              {
                label: 'Closed',
                value: 'closed',
              },
              {
                label: 'Void',
                value: 'void',
              },
            ]}
            value={filters.status}
            onChange={(e) => {
              setResetPaginationToggle(e.target.value.length > 0)
              setFilters({ ...filters, status: e.target.value })
            }}
          />
        </DcFlexBox>
        <PixelFlexBox
          Width="50%"
          height="none"
          justifyContent="flex-end"
          alignItems="center"
        >
          <DcButton
            size="sm"
            variant="outline-primary"
            onClick={() => {
              setInvoiceId('new')
              setShowModal(true)
            }}
          >
            <PixelIcon color="inherit" icon={faPlus} /> Add New
          </DcButton>
        </PixelFlexBox>
      </DcFlexBox>
      <PixelPageContainer style={{ padding: '10px' }}>
        <Section style={{ padding: '10px' }}>
          <SectionContent style={{ margin: '0 0 20px 0' }} padding="0px">
            <InvoicesStats stats={invoiceStats} />
          </SectionContent>
          <DcDiv>
            <PixelTable
              highlightOnHover={true}
              isSearchable={true}
              pointerOnHover={true}
              columns={columns}
              data={invoices}
              progressPending={tableLoading}
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={handlePerRowsChange}
              paginationRowsPerPageOptions={[50, 100, 250, 500]}
              paginationResetDefaultPage={resetPaginationToggle}
              paginationDefaultPage={Number(filters.page) + 1}
              onChangePage={handlePageChange}
              onSort={handleSort}
              sortServer
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 390px)"
              customStyles={{
                subHeader: {
                  style: {
                    padding: '0px !important',
                  },
                },
              }}
              onRowClicked={onRowClicked}
            />
          </DcDiv>
        </Section>
      </PixelPageContainer>
      <DcFlexBox
        zIndex="100"
        width="100%"
        height="100%"
        onClick={() => setShowModal(false)}
        right={showModal ? '0' : '-100%'}
        backgroundColor="rgba(46, 49, 74, 0.4)"
        opacity="1"
        position="absolute"
      ></DcFlexBox>

      <DcFlexBox
        zIndex="100"
        width="70%"
        height="100%"
        right={showModal ? '0' : '-100%'}
        top="0"
        border={`1px solid ${colors.grayBorderLight}`}
        backgroundColor={colors.white}
        padding="10px"
        position="absolute"
        flexDirection="column"
        style={{ transition: 'right 0.5s ease-in-out' }}
      >
        <DcFlexBox
          height="30px"
          width="100%"
          backgroundColor={colors.white}
          padding="0 10px 10px 10px"
          justifyContent="space-between"
        >
          <DcDiv padding="0 10px 0 0">
            <DcText style={{ fontWeight: '700' }}>
              {invoiceId == 'new' ? 'Add New Invoice' : 'Invoice Detail'}
            </DcText>
          </DcDiv>
          <DcDiv
            hoverStyle={{
              color: $DCprimaryColor,
              cursor: 'pointer',
            }}
            color="rgba(0, 0, 0, 0.5)"
            width="15px"
          >
            <DcIcon
              onClick={() => {
                setShowModal(false)
              }}
              color="inherit"
              height="50px"
              width="40px"
              fontSize="30px"
              icon={faXmark}
            />
          </DcDiv>
        </DcFlexBox>
        <DcDiv height="calc(100vh - 30px)">
          <InvoiceDetail
            invoiceId={invoiceId}
            setInvoiceId={setInvoiceId}
            showModal={showModal}
            setShowModal={setShowModal}
          />
        </DcDiv>
      </DcFlexBox>
    </>
  )
}

const CustomerInfo = styled.div`
  white-space: break-spaces !important;
`
export default Invoice
